import React from 'react';

import type { InventoryAgeingRow } from '../../api/inventory';
import { semanticColors } from '@styles/design-system';
import { dataSheetTokens, type DataSheetColumn } from '../ui/DataSheet';

import { ageBucketLabels, OVER_THRESHOLD_DAYS, unitsOlderThan } from './inventoryAgeing';
import { GroupCell, SharePill } from './AgeingCells';

export type AgeingCellTarget = { row: InventoryAgeingRow; bucketIndex: number | 'older' };

/** Bucket colours, youngest to oldest; the last is reused for "older than history". */
export const AGE_BUCKET_COLORS = ['#8CC5A8', '#C7DDA0', '#E9D27A', '#D99A3A', '#B8412F'];
export const OLDER_THAN_HISTORY_COLOR = '#CBD5E1';

export const formatUnits = (value: number | null | undefined): React.ReactNode =>
    value == null ? '—' : value === 0 ? (
        <span style={{ color: dataSheetTokens.colors.textMuted }}>0</span>
    ) : (
        Math.round(value).toLocaleString()
    );

export const formatDrr = (value: number): string =>
    value >= 100 ? Math.round(value).toLocaleString() : value.toFixed(1);

export const formatReceiptDate = (value: string | null): string =>
    value
        ? new Date(`${value}T00:00:00`).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
          })
        : '—';



export type AgeingColumnArgs = {
    edges: number[];
    receiptsFromDate: string | null;
    /** Label of the active rollup dimension; null when showing SKU rows. */
    rollupLabel: string | null;
    hiddenSet: Set<string>;
    dohLabel: string;
    formatDoh: (doh: number | null) => React.ReactNode;
    /** Absent for the CSV build, which never invokes a click. */
    onCellClick?: (target: AgeingCellTarget) => void;
    /** WAREHOUSE scope: a warehouse column, and no DRR / DoH (internal DRR has no per-warehouse reading). */
    byWarehouse?: boolean;
};

/**
 * One builder for the grid and the CSV, so the two cannot drift. Bucket columns are keyed
 * `age_<i>`; the rest by what they show. `valueAccessor` is set on every numeric column
 * so header sort, filters and highlights operate on the number, not the rendered cell.
 */
export const buildAgeingColumns = ({
    edges,
    receiptsFromDate,
    rollupLabel,
    hiddenSet,
    dohLabel,
    formatDoh,
    onCellClick,
    byWarehouse = false,
}: AgeingColumnArgs): DataSheetColumn<InventoryAgeingRow>[] => {
    const labels = ageBucketLabels(edges);
    const lastBucket = edges.length;
    const clickable = (
        row: InventoryAgeingRow,
        value: number,
        bucketIndex: number | 'older',
        color?: string,
    ): React.ReactNode => {
        if (!value || !onCellClick || row.subRows?.length) {
            return color && value ? <span style={{ color, fontWeight: 600 }}>{formatUnits(value)}</span> : formatUnits(value);
        }
        return (
            <span
                onClick={(event) => {
                    event.stopPropagation();
                    onCellClick({ row, bucketIndex });
                }}
                style={{
                    cursor: 'pointer',
                    textDecoration: 'underline dotted',
                    color: color ?? 'inherit',
                    fontWeight: color ? 600 : undefined,
                }}
            >
                {formatUnits(value)}
            </span>
        );
    };

    const columns: DataSheetColumn<InventoryAgeingRow>[] = [
        {
            key: 'product_id',
            header: rollupLabel ?? 'Product ID',
            sticky: true,
            width: 170,
            valueAccessor: (row) => row.product_id,
            render: (_value, row) => (row.subRows?.length ? <GroupCell row={row} /> : row.product_id),
        },
        {
            key: 'name',
            header: 'Name',
            sticky: true,
            width: 220,
            valueAccessor: (row) => row.name,
            render: (_value, row) => row.name,
        },
        ...(byWarehouse
            ? [
                  {
                      key: 'warehouse',
                      header: 'Warehouse',
                      width: 160,
                      valueAccessor: (row: InventoryAgeingRow) => row.warehouse_name ?? '',
                      render: (_value: unknown, row: InventoryAgeingRow) => row.warehouse_name ?? '—',
                  } satisfies DataSheetColumn<InventoryAgeingRow>,
              ]
            : []),
        {
            key: 'category',
            header: 'Category',
            width: 130,
            valueAccessor: (row) => row.category ?? '',
            render: (_value, row) => row.category ?? '—',
        },
        {
            key: 'product_group',
            header: 'Product Group',
            width: 150,
            valueAccessor: (row) => row.product_group ?? '',
            render: (_value, row) => row.product_group ?? '—',
        },
        {
            key: 'on_hand',
            header: 'On hand',
            align: 'right',
            width: 100,
            valueAccessor: (row) => row.internal.stock ?? 0,
            render: (_value, row) => formatUnits(row.internal.stock),
        },
        ...edges.map((_edge, index) => ({
            key: `age_${index}`,
            header: labels[index],
            group: 'Units by age (days)',
            align: 'right' as const,
            width: 90,
            valueAccessor: (row: InventoryAgeingRow) => row.age.units[index] ?? 0,
            render: (_value: unknown, row: InventoryAgeingRow) =>
                clickable(row, row.age.units[index] ?? 0, index),
        })),
        {
            key: `age_${lastBucket}`,
            header: labels[lastBucket],
            group: 'Units by age (days)',
            align: 'right',
            width: 90,
            valueAccessor: (row) => row.age.units[lastBucket] ?? 0,
            render: (_value, row) =>
                clickable(row, row.age.units[lastBucket] ?? 0, lastBucket, semanticColors.critical.primary),
        },
        {
            key: 'age_older',
            // Pooled, the remainder is what predates our receipt history; per warehouse the
            // replay has already tried every receipt and transfer, so it is simply untraced.
            header: byWarehouse ? 'Untraced' : receiptsFromDate ? `Before ${formatReceiptDate(receiptsFromDate)}` : 'Older than history',
            group: 'Units by age (days)',
            align: 'right',
            width: 130,
            valueAccessor: (row) => row.age.older_than_history,
            render: (_value, row) => clickable(row, row.age.older_than_history, 'older'),
        },
        {
            key: 'over_threshold',
            header: `Over ${OVER_THRESHOLD_DAYS}d`,
            align: 'right',
            width: 90,
            valueAccessor: (row) =>
                row.internal.stock
                    ? Math.round((100 * unitsOlderThan(row.age, edges, OVER_THRESHOLD_DAYS)) / row.internal.stock)
                    : 0,
            render: (_value, row) => {
                const stock = row.internal.stock ?? 0;
                if (!stock) return '—';
                return <SharePill share={Math.round((100 * unitsOlderThan(row.age, edges, OVER_THRESHOLD_DAYS)) / stock)} />;
            },
            csvValue: (_value, row) =>
                row.internal.stock
                    ? `${Math.round((100 * unitsOlderThan(row.age, edges, OVER_THRESHOLD_DAYS)) / row.internal.stock)}%`
                    : '',
        },
        {
            key: 'wtd_age',
            header: 'Wtd age',
            align: 'right',
            width: 90,
            valueAccessor: (row) => row.age.weighted_age_days,
            render: (_value, row) =>
                row.age.weighted_age_days == null ? '—' : `${Math.round(row.age.weighted_age_days)} d`,
        },
        {
            key: 'oldest',
            header: 'Oldest receipt',
            align: 'right',
            width: 120,
            valueAccessor: (row) => row.age.oldest_receipt_date,
            render: (_value, row) => formatReceiptDate(row.age.oldest_receipt_date),
        },
        {
            key: 'drr',
            header: 'DRR',
            group: 'Rate',
            align: 'right',
            width: 80,
            valueAccessor: (row) => row.internal.drr,
            render: (_value, row) => formatDrr(row.internal.drr),
        },
        {
            key: 'doh',
            header: dohLabel,
            group: 'Rate',
            align: 'right',
            width: 80,
            valueAccessor: (row) => row.internal.doh,
            render: (_value, row) => formatDoh(row.internal.doh),
        },
    ];
    return columns.filter(
        (column) => !hiddenSet.has(column.key) && !(byWarehouse && (column.key === 'drr' || column.key === 'doh')),
    );
};

export const AGEING_DEFAULT_ON_DEFS = [
    { key: 'name', label: 'Name' },
    { key: 'product_group', label: 'Product group' },
    { key: 'on_hand', label: 'On hand' },
    { key: 'over_threshold', label: `Over ${OVER_THRESHOLD_DAYS}d share` },
    { key: 'wtd_age', label: 'Weighted age' },
    { key: 'drr', label: 'DRR' },
    { key: 'doh', label: 'DoH' },
];
/** The picker's list in the WAREHOUSE scope: the warehouse column in, the rate columns out. */
export const AGEING_WAREHOUSE_ON_DEFS = [
    { key: 'name', label: 'Name' },
    { key: 'warehouse', label: 'Warehouse' },
    ...AGEING_DEFAULT_ON_DEFS.filter((def) => !['name', 'drr', 'doh'].includes(def.key)),
];
export const AGEING_DEFAULT_OFF_DEFS = [
    { key: 'category', label: 'Category' },
    { key: 'oldest', label: 'Oldest receipt' },
];
export const AGEING_DEFAULT_HIDDEN = AGEING_DEFAULT_OFF_DEFS.map((def) => def.key);
